import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Icon,
  IconButton,
  LinearProgress,
  Paper,
  Snackbar,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  TextField,
  Tooltip,
  Typography,
} from "@material-ui/core"
import { Alert } from "@material-ui/lab"
import {
  CountDocuments,
  DocumentFilters,
  DocumentPicker,
  FixedLoadingIndicator,
  LabelDisplayedRows,
  RefundsCell,
  Search,
  ServerErrorHandler,
} from "components"
import { AuthContext, DocumentContext, ManagementContext } from "context"
import {
  DASHBOARD_TYPES,
  DEFAULT_DOCUMENTS_TABLE_COLUMNS,
  DOCUMENT_STATES,
  DOCUMENT_TITLE,
  DOCUMENT_TYPE_CODES,
  DOCUMENT_TYPE_IDS,
  DOCUMENT_VIEW_PATH,
  DOCUMENTS_RECEIVED_PATH,
  DOCUMENTS_RECEPTION_TABLE_COLUMNS,
  FIELDS,
  FINISH_RECEPTION_DOCUMENTS_TABLE_COLUMNS,
  FINISHED_PRINTED_INVOICES_COLUMNS,
  getCustomerByIdentificationService,
  getDocumentByIdService,
  getPurchaseOrderByIdService,
  MEDIUM_ROWS_PER_PAGINATION_OPTIONS,
  notificationClientRetentionService,
  reprocessPendingDocumentsService,
  PENDING_DOCUMENTS_TABLE_COLUMNS,
  PURCHASE_ORDER_PATH,
  PURCHASE_ORDER_VIEW_PATH,
  ROOT_TITLE,
  SINGLE_RECEPTION_DOCUMENTS_TABLE_COLUMNS_ON_HOLD,
} from "lib"
import moment from "moment"
import React from "react"
import { Helmet } from "react-helmet"
import { useHistory } from "react-router"
import { IDocument, IMessageConfig } from "types"

interface Props {
  initialCode?: string
  initialState?: number
  initialHistoryType?: number
  redirectOnApprove?: string
  redirectOnReject?: string
  hideState?: boolean
  hideType?: boolean
  forEmission?: boolean
  forReception?: boolean
  showCounts?: boolean
  issuingCompany?: boolean
  receivingCompany?: boolean
  countPath?: string
  createRetentionOnly?: boolean
  showOnHoldCount?: boolean
}

const DocumentsReceivedDashboard = (props: Props) => {
  const {
    showOnHoldCount,
    initialCode,
    issuingCompany,
    initialState,
    redirectOnApprove,
    hideState,
    hideType,
    forEmission,
    forReception,
    showCounts,
    redirectOnReject,
    initialHistoryType,
    receivingCompany,
    createRetentionOnly,
  } = props

  const {
    documents,
    company,
    conditions,
    documentFilters: filters,
    setFilters,
    documentsModel,
    documentTypes,
    getDocumentsByFilters,
    totalDocuments,
    getNextDocumentsByFilters,
    getDocumentsByInitialFilters,
  } = React.useContext(DocumentContext)

  const {
    createSortHandler,
    handleChangePage,
    handleChangeRowsPerPage,
    order,
    orderBy,
    page,
    rowsPerPage,
  } = documentsModel
  const history = useHistory()

  const {
    refreshIdentificationTypes,
    refreshTaxes,
    refreshTaxTypes,
    identificationTypes,
    taxTypes,
    taxes,
    loadInitialCustomers,
  } = React.useContext(ManagementContext)

  const { user } = React.useContext(AuthContext)

  const [loading, setLoading] = React.useState<boolean>(false)
  const [initialLoading, setInitialLoading] = React.useState<boolean>(false)
  const [sendingNotify, setSendingNotify] = React.useState<boolean>(false)
  const [reprocessing, setReprocessing] = React.useState<boolean>(false)
  const [open, setOpen] = React.useState<boolean>(false)
  const [errorEmail, setErrorEmail] = React.useState("")
  const [error, setError] = React.useState<boolean>(false)
  const [messageConfig, setMessageConfig] = React.useState<IMessageConfig>({
    open: false,
    message: "",
    severity: "info",
  })
  const [emails, setEmails] = React.useState<string[]>([])
  const [newEmail, setNewEmail] = React.useState<string>("")
  const [notifyDocumentId, setNotifyDocumentId] = React.useState<number | null>(null)
  const [serialInvoice, setSerialInvoice] = React.useState<string>("")
  const [openPicker, setOpenPicker] = React.useState<boolean>(false)
  const [openFilters, setOpenFilters] = React.useState<boolean>(false)
  const [copiedDocId, setCopiedDocId] = React.useState<number | null>(null)
  const [customerName, setCustomerName] = React.useState<string>("")
  const [successDialog, setSuccessDialog] = React.useState<boolean>(false)
  const handleOpen = () => setOpen(true)
  const handleClose = () => setOpen(false)

  React.useEffect(() => {
    if (successDialog) {
      const timer = setTimeout(() => setSuccessDialog(false), 2500)
      return () => clearTimeout(timer)
    }
  }, [successDialog])

  const handleFilter = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    try {
      setLoading(true)
      await getDocumentsByFilters(DASHBOARD_TYPES.receivedDocuments)
      handleChangePage(undefined, 0)
      setLoading(false)
    } catch (error) {
      setLoading(false)
      setError(true)
    }
  }

  const documentsTable = React.useMemo((): IDocument[] => {
    return initialLoading ? [] : documents
  }, [documents, initialLoading])

  const isOnHoldState = React.useMemo(() => {
    return initialState === DOCUMENT_STATES.onHold
  }, [initialState])

  const isFinishedState = React.useMemo(() => {
    return initialState === DOCUMENT_STATES.finished
  }, [initialState])

  const isPendingState = React.useMemo(() => {
    return initialState === DOCUMENT_STATES.pending
  }, [initialState])

  const HEADERS = React.useMemo(() => {
    if (!issuingCompany) return DOCUMENTS_RECEPTION_TABLE_COLUMNS
    if (isOnHoldState) return SINGLE_RECEPTION_DOCUMENTS_TABLE_COLUMNS_ON_HOLD
    if (isPendingState) return PENDING_DOCUMENTS_TABLE_COLUMNS
    if (isFinishedState) return FINISH_RECEPTION_DOCUMENTS_TABLE_COLUMNS
    return DEFAULT_DOCUMENTS_TABLE_COLUMNS
  }, [issuingCompany, isOnHoldState, isPendingState, isFinishedState])

  const handleNewPage = async (pageNumber: number, offset: number) => {
    try {
      setLoading(true)
      await getNextDocumentsByFilters(offset, pageNumber, DASHBOARD_TYPES.receivedDocuments)
      setLoading(false)
    } catch (error) {
      setLoading(false)
      setError(true)
    }
  }

  const handleSelect = async (document: IDocument) => {
    try {
      setLoading(true)
      if (identificationTypes.length === 0) {
        refreshIdentificationTypes()
      }
      if (taxTypes.length === 0) {
        refreshTaxTypes()
      }
      if (taxes.length === 0) {
        refreshTaxes()
      }
      const response = await getDocumentByIdService(document.documentId)
      history.push({
        state: { document: response, redirectOnApprove, redirectOnReject },
        pathname: `${DOCUMENTS_RECEIVED_PATH}${DOCUMENT_VIEW_PATH}/${document.documentId}`,
      })
      setLoading(false)
    } catch (error) {
      console.log(error)
      setMessageConfig({
        open: true,
        message: "No se pudo obtener información del documento selecionado...",
        severity: "error",
      })
      setLoading(false)
    }
  }

  const handleClearFilters = async () => {
    try {
      setLoading(true)
      await getDocumentsByInitialFilters(
        initialCode,
        initialState,
        issuingCompany ? company?.identification : undefined,
        initialHistoryType,
        undefined,
        DASHBOARD_TYPES.receivedDocuments,
      )
      handleChangePage(undefined, 0)
      setLoading(false)
    } catch (error) {
      setLoading(false)
      setError(true)
    }
  }

  const isFinishedInvoice = React.useCallback(() => {
    return (
      initialState === DOCUMENT_STATES.finished &&
      (initialCode === DOCUMENT_TYPE_CODES.printedInvoice ||
        initialCode === DOCUMENT_TYPE_CODES.electronicInvoice)
    )
  }, [initialState, initialCode])

  const isRetentionReceipt = React.useCallback(() => {
    return initialCode === DOCUMENT_TYPE_CODES.retentionReceipt
  }, [initialCode])

  const isReimbursementInvoice = React.useCallback(() => {
    return initialCode === DOCUMENT_TYPE_CODES.reimbursementInvoice
  }, [initialCode])

  const isPurchaseSettlement = React.useCallback(() => {
    return initialCode === DOCUMENT_TYPE_CODES.purchaseSettlement
  }, [initialCode])

  const applyingFilters = React.useCallback((): boolean => {
    return Boolean(
      filters.conditions.length > 0 ||
      filters.documentTypes.length > 0 ||
      Boolean(filters.rucOrName) ||
      Boolean(filters.serialNumber) ||
      filters.documentRange.start !== null ||
      filters.documentRange.end !== null ||
      filters.receptionRange.start !== null ||
      filters.receptionRange.end !== null ||
      filters.rucOrNameReceptor !== null,
    )
  }, [filters])

  const handlePurchaseOrder = async (id: number) => {
    try {
      setLoading(true)
      const purchaseOrder = await getPurchaseOrderByIdService(id)
      history.push({
        state: { purchaseOrder },
        pathname: `${PURCHASE_ORDER_PATH}${PURCHASE_ORDER_VIEW_PATH}/${purchaseOrder.document.documentId}`,
      })
      setLoading(false)
    } catch (error) {
      setLoading(false)
      setMessageConfig({
        open: true,
        message: "No se pudo obtener información del documento selecionado...",
        severity: "error",
      })
    }
  }

  const obtenerNombreDocumento = (row: IDocument) => {
    const name = documentTypes.find(
      (d) => d.documentTypeId === row.documentTypeId,
    )?.documentName
    if (name === "COMPROBANTE DE RETENCIÓN") {
      if (row.sriReportId === 2) {
        return "RETENCIÓN RENTA"
      } else {
        return "RETENCIÓN IVA"
      }
    } else {
      return name
    }
  }

  const handleNotifyClient = async () => {
    setSendingNotify(true)

    try {
      const { status, data } = await notificationClientRetentionService(
        emails.join(","),
        serialInvoice,
        customerName,
        notifyDocumentId!,
        user?.userId ?? null,
      )
      if (status === 200) {
        handleCloseNotification()
        setSuccessDialog(true)
      } else {
        setMessageConfig({ open: true, message: data, severity: "error" })
      }
    } catch (error) {
      console.error("Error al notificar al cliente", error)

      setMessageConfig({
        open: true,
        message: "Ocurrió un error al notificar al cliente",
        severity: "error",
      })
    } finally {
      setSendingNotify(false)
    }
  }

  const initLoadCustomers = async () => {
    try {
      await loadInitialCustomers()
    } catch (error) {
      setError(true)
    }
  }

  const handleReprocessPending = async () => {
    try {
      setReprocessing(true)
      await reprocessPendingDocumentsService()
      setMessageConfig({
        open: true,
        message: "Reproceso ejecutado correctamente.",
        severity: "success",
      })
      await getDocumentsByFilters(DASHBOARD_TYPES.receivedDocuments)
    } catch (error) {
      setMessageConfig({
        open: true,
        message: "Ocurrió un error al reprocesar los documentos pendientes.",
        severity: "error",
      })
    } finally {
      setReprocessing(false)
    }
  }

  const handleCopyRuc = (ruc: string, documentId: number) => {
    navigator.clipboard.writeText(ruc).then(() => {
      setCopiedDocId(documentId)
      setTimeout(() => setCopiedDocId(null), 1500)
    })
  }

  React.useEffect(() => {
    initLoadCustomers()
  }, [])

  React.useEffect(() => {
    const initScreen = async () => {
      const receivedRuc = receivingCompany ? company?.identification : undefined
      const companyRuc = issuingCompany ? company?.identification : undefined
      try {
        if (history.action === "REPLACE" || history.action === "PUSH") {
          setInitialLoading(true)
          await getDocumentsByInitialFilters(
            initialCode,
            initialState,
            companyRuc,
            initialHistoryType,
            receivedRuc,
            DASHBOARD_TYPES.receivedDocuments,
          )
          handleChangePage(undefined, 0)
          setInitialLoading(false)
        }
      } catch (error) {
        setInitialLoading(false)
        setError(true)
      }
    }
    initScreen()
  }, [
    initialCode,
    getDocumentsByInitialFilters,
    history,
    initialState,
    handleChangePage,
    issuingCompany,
    company,
    initialHistoryType,
    receivingCompany,
  ])

  const handleCloseNotification = () => {
    setNewEmail("")
    setErrorEmail("")
    handleClose()
    setEmails([])
  }

  const handleAddEmail = () => {
    if (!newEmail) return
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

    if (!emailRegex.test(newEmail)) {
      setErrorEmail("No es un correo válido")
      return
    }

    if (emails.includes(newEmail)) {
      setErrorEmail("El correo ya está agregado")
      return
    }
    setEmails([...emails, newEmail])
    setNewEmail("")
    setErrorEmail("")
  }

  const handleOpenNotification = async (companyRuc: string) => {
    const customer = await getCustomerByIdentificationService(companyRuc)
    if (!customer) {
      return
    }
    const emailsFromContacts =
      customer.contacts?.filter((c) => c.email).map((c) => c.email!) ?? []
    setEmails(emailsFromContacts)
    handleOpen()
  }

  return (
    <Paper className="flex flex-col h-full overflow-hidden p-4 pb-0">
      <Helmet>
        <title>{`${ROOT_TITLE} - ${DOCUMENT_TITLE}`}</title>
      </Helmet>
      <div className="flex items-center">
        <form className="flex flex-col w-full" onSubmit={handleFilter}>
          {showCounts && (
            <CountDocuments
              countPath={DOCUMENTS_RECEIVED_PATH}
              isOnHold={showOnHoldCount}
            />
          )}
          <div className="flex items-center justify-between w-full">
            <div className="flex">
              <Search
                onChange={(e) =>
                  setFilters({ ...filters, serialNumber: e.target.value })
                }
                query={filters.serialNumber ?? ""}
                onClear={() => setFilters({ ...filters, serialNumber: "" })}
                placeholer="Busca por num de serie o asiento"
                width={280}
              />
              {!issuingCompany ? (
                <div className="ml-2">
                  <Search
                    onChange={(e) =>
                      setFilters({ ...filters, rucOrName: e.target.value })
                    }
                    query={filters.rucOrName}
                    onClear={() => setFilters({ ...filters, rucOrName: "" })}
                    placeholer="RIF o nombre..."
                    width={250}
                  />
                </div>
              ) : (
                <div className="ml-2">
                  <Search
                    onChange={(e) =>
                      setFilters({
                        ...filters,
                        rucOrNameReceptor: e.target.value,
                      })
                    }
                    query={filters.rucOrNameReceptor}
                    onClear={() =>
                      setFilters({ ...filters, rucOrNameReceptor: "" })
                    }
                    placeholer="RIF o nombre de receptor..."
                    width={250}
                  />
                </div>
              )}
              <Button
                size="small"
                style={{ marginLeft: 20 }}
                disableElevation
                color={applyingFilters() ? "primary" : undefined}
                onClick={() => setOpenFilters((current) => !current)}
              >
                <Icon fontSize="small" style={{ marginRight: 5 }}>
                  {openFilters ? "expand_less" : "expand_more"}
                </Icon>
                <span className="pr-2">{`${
                  openFilters ? "Menos filtros" : "Más filtros"
                }`}</span>
              </Button>
              {applyingFilters() && (
                <Tooltip arrow title="Resetear filtros por defecto">
                  <IconButton
                    size="small"
                    onClick={handleClearFilters}
                    disabled={loading || initialLoading}
                  >
                    <Icon fontSize="small" className="mx-2">
                      restart_alt
                    </Icon>
                  </IconButton>
                </Tooltip>
              )}
              <Button
                variant="contained"
                color="primary"
                size="small"
                style={{ marginLeft: 20 }}
                disableElevation
                type="submit"
                disabled={loading || initialLoading}
              >
                {"Aplicar"}
              </Button>
            </div>
            {isPendingState && totalDocuments > 0 && (
              <Button
                variant="contained"
                color="secondary"
                size="small"
                disableElevation
                disabled={loading || initialLoading || reprocessing}
                startIcon={
                  reprocessing
                    ? <CircularProgress color="inherit" size={16} />
                    : <Icon fontSize="small">replay</Icon>
                }
                onClick={handleReprocessPending}
              >
                Reprocesar Pendientes
              </Button>
            )}
          </div>
          <Collapse in={openFilters}>
            <DocumentFilters
              hideState={hideState}
              hideType={hideType}
              forEmission={forEmission}
              forReception={forReception}
            />
          </Collapse>
        </form>
      </div>
      <div className="flex h-full flex-col mt-4 overflow-hidden">
        {initialLoading && <LinearProgress />}
        <div className="flex flex-grow overflow-auto">
          <TableContainer>
            <Table stickyHeader size="small">
              <TableHead>
                <TableRow>
                  {HEADERS.map(
                    (headCell) =>
                      ((createRetentionOnly &&
                        headCell.id !== FIELDS.processStatus.key) ||
                        !createRetentionOnly) && (
                        <TableCell
                          key={headCell.id}
                          align={"left"}
                          padding={"default"}
                          sortDirection={
                            orderBy === headCell.id ? order : false
                          }
                        >
                          <TableSortLabel
                            active={orderBy === headCell.id}
                            direction={orderBy === headCell.id ? order : "asc"}
                            onClick={createSortHandler(headCell.id as any)}
                            style={{
                              fontSize: "0.85em",
                              width:
                                headCell.id === FIELDS.companyName.key ||
                                headCell.id === FIELDS.receiverName.key
                                  ? 320
                                  : headCell.id === FIELDS.flowStatus.key ||
                                      headCell.id === FIELDS.processStatus.key
                                    ? 250
                                    : "max-content",
                            }}
                          >
                            {headCell.label}
                            {orderBy === headCell.id ? (
                              <span className="hidden">
                                {order === "desc"
                                  ? "sorted descending"
                                  : "sorted ascending"}
                              </span>
                            ) : null}
                          </TableSortLabel>
                        </TableCell>
                      ),
                  )}
                  {isFinishedInvoice() && (
                    <React.Fragment>
                      {FINISHED_PRINTED_INVOICES_COLUMNS.map((headCell) => (
                        <TableCell
                          key={headCell.id}
                          align={"left"}
                          padding={"default"}
                          sortDirection={
                            orderBy === headCell.id ? order : false
                          }
                        >
                          <TableSortLabel
                            active={orderBy === headCell.id}
                            direction={orderBy === headCell.id ? order : "asc"}
                            onClick={createSortHandler(headCell.id as any)}
                            style={{
                              fontSize: "0.85em",
                              width:
                                headCell.id === FIELDS.companyName.key ||
                                headCell.id === FIELDS.receiverName.key
                                  ? 320
                                  : headCell.id === FIELDS.flowStatus.key
                                    ? 200
                                    : "max-content",
                            }}
                          >
                            {headCell.label}
                            {orderBy === headCell.id ? (
                              <span className="hidden">
                                {order === "desc"
                                  ? "sorted descending"
                                  : "sorted ascending"}
                              </span>
                            ) : null}
                          </TableSortLabel>
                        </TableCell>
                      ))}
                    </React.Fragment>
                  )}
                  {isRetentionReceipt() && (
                    <TableCell
                      key={FIELDS.invoiceNumber.name}
                      align={"left"}
                      padding={"default"}
                      sortDirection={
                        orderBy === FIELDS.invoiceNumber.name ? order : false
                      }
                    >
                      <TableSortLabel
                        active={orderBy === FIELDS.invoiceNumber.name}
                        direction={
                          orderBy === FIELDS.invoiceNumber.name ? order : "asc"
                        }
                        onClick={createSortHandler(
                          FIELDS.invoiceNumber.name as any,
                        )}
                        style={{ fontSize: "0.85em", width: 200 }}
                      >
                        {FIELDS.invoiceNumber.name}
                        {orderBy === FIELDS.invoiceNumber.name ? (
                          <span className="hidden">
                            {order === "desc"
                              ? "sorted descending"
                              : "sorted ascending"}
                          </span>
                        ) : null}
                      </TableSortLabel>
                    </TableCell>
                  )}

                  {isReimbursementInvoice() && (
                    <TableCell
                      key={FIELDS.sustentions.name}
                      align={"left"}
                      padding={"default"}
                      sortDirection={
                        orderBy === FIELDS.sustentions.name ? order : false
                      }
                    >
                      <TableSortLabel
                        active={orderBy === FIELDS.sustentions.name}
                        direction={
                          orderBy === FIELDS.sustentions.name ? order : "asc"
                        }
                        onClick={createSortHandler(
                          FIELDS.sustentions.name as any,
                        )}
                        style={{ fontSize: "0.85em", width: 200 }}
                      >
                        {FIELDS.sustentions.name}
                        {orderBy === FIELDS.sustentions.name ? (
                          <span className="hidden">
                            {order === "desc"
                              ? "sorted descending"
                              : "sorted ascending"}
                          </span>
                        ) : null}
                      </TableSortLabel>
                    </TableCell>
                  )}

                  {isPurchaseSettlement() && (
                    <TableCell
                      key={FIELDS.foreignExpensesLiquidation.name}
                      align={"left"}
                      padding={"default"}
                      sortDirection={
                        orderBy === FIELDS.foreignExpensesLiquidation.name
                          ? order
                          : false
                      }
                    >
                      <TableSortLabel
                        active={
                          orderBy === FIELDS.foreignExpensesLiquidation.name
                        }
                        direction={
                          orderBy === FIELDS.foreignExpensesLiquidation.name
                            ? order
                            : "asc"
                        }
                        onClick={createSortHandler(
                          FIELDS.foreignExpensesLiquidation.name as any,
                        )}
                        style={{ fontSize: "0.85em", width: 250 }}
                      >
                        {FIELDS.foreignExpensesLiquidation.name}
                        {orderBy === FIELDS.foreignExpensesLiquidation.name ? (
                          <span className="hidden">
                            {order === "desc"
                              ? "sorted descending"
                              : "sorted ascending"}
                          </span>
                        ) : null}
                      </TableSortLabel>
                    </TableCell>
                  )}
                  <TableCell padding={"default"} />
                </TableRow>
              </TableHead>

              <TableBody>
                {documentsTable.map((row, index) => {
                  return (
                    <TableRow
                      hover
                      tabIndex={-1}
                      key={row.documentId.toString()}
                      className="cursor-pointer"
                      onClick={
                        loading || initialLoading
                          ? undefined
                          : () => handleSelect(row)
                      }
                    >
                      <TableCell
                        className="truncate"
                        component="th"
                        scope="row"
                        padding="none"
                      >
                        {obtenerNombreDocumento(row)}
                      </TableCell>

                      <TableCell
                        className="truncate"
                        align="left"
                        padding="default"
                      >
                        {row.serialNumber.length === 15
                          ? `${row.serialNumber.substring(
                              0,
                              3,
                            )}-${row.serialNumber.substring(
                              3,
                              6,
                            )}-${row.serialNumber.substring(6, 15)}`
                          : row.serialNumber}
                      </TableCell>

                      <TableCell padding="default" align="left">
                        {/* Se añade una hora a la fecha, ya que el servidor de producción tiene una hora adelantada
                                                    y el documentDate en la base tiene como hora 00:00:00 -5, y el servidor tiene +1 */}
                        {moment(new Date(row.documentDate))
                          .add(1, "hour")
                          .format("DD-MM-YYYY")
                          .toString()}
                      </TableCell>

                      {isFinishedState && (
                        <TableCell padding="default" align="left">
                          {row.authorizationDate
                            ? moment(new Date(row.authorizationDate))
                                .add(1, "hour")
                                .format("DD-MM-YYYY")
                                .toString()
                            : ""}
                        </TableCell>
                      )}

                      <TableCell padding="default" align="left">
                        <Box
                          display="flex"
                          alignItems="center"
                          style={{ gap: 6 }}
                        >
                          {issuingCompany ? row.receiverRuc : row.companyRuc}
                          {issuingCompany && isOnHoldState && (
                            <Tooltip
                              title={
                                copiedDocId === row.documentId
                                  ? "Copiado"
                                  : "Copiar RUC"
                              }
                            >
                              <IconButton
                                size="small"
                                onClick={(e) => {
                                  e.stopPropagation()
                                  handleCopyRuc(row.receiverRuc, row.documentId)
                                }}
                                style={{ padding: 2 }}
                              >
                                {copiedDocId === row.documentId ? (
                                  <Icon
                                    style={{
                                      fontSize: 18,
                                      fontWeight: "bold",
                                      color: "#1D9E75",
                                    }}
                                  >
                                    check
                                  </Icon>
                                ) : (
                                  <Icon style={{ fontSize: 18 }}>
                                    file_copy
                                  </Icon>
                                )}
                              </IconButton>
                            </Tooltip>
                          )}
                        </Box>
                      </TableCell>

                      <TableCell
                        className="truncate"
                        padding="default"
                        align="left"
                      >
                        {issuingCompany ? row.receiverName : row.companyName}
                      </TableCell>

                      {isOnHoldState && (
                        <TableCell padding="default" align="left">
                          <Box>
                            <Button
                              endIcon={<Icon>notifications_active</Icon>}
                              size="small"
                              style={{ height: 25, color: "" }}
                              variant="contained"
                              color="primary"
                              disableElevation
                              onClick={(event) => {
                                event.stopPropagation()
                                event.preventDefault()
                                setSerialInvoice(row.serialNumber)
                                setCustomerName(row.receiverName)
                                setNotifyDocumentId(row.documentId)
                                handleOpenNotification(row.receiverRuc)
                              }}
                            >
                              Notificar
                            </Button>
                          </Box>
                        </TableCell>
                      )}

                      {!issuingCompany && (
                        <TableCell padding="default" align="left">
                          {
                            conditions.find(
                              (c) => c.conditionId === row.flowStatus,
                            )?.name
                          }
                        </TableCell>
                      )}

                      {issuingCompany && (
                        <TableCell
                          padding="default"
                          align="left"
                          style={{ whiteSpace: "nowrap" }}
                        >
                          {row.error}
                        </TableCell>
                      )}

                      {isFinishedInvoice() && (
                        <React.Fragment>
                          <TableCell
                            onClick={(e) => {
                              const id = row.associated.find((r) => {
                                const type = documentTypes.find(
                                  (d) => d.documentTypeId === r.documentTypeId,
                                )
                                return (
                                  r.documentTypeId === type?.documentTypeId &&
                                  type.documentCode ===
                                    DOCUMENT_TYPE_CODES.purchaseOrder
                                )
                              })?.documentId
                              if (id) {
                                e.stopPropagation()
                                handlePurchaseOrder(id)
                              }
                            }}
                            className="document-link"
                            padding="default"
                            align="left"
                          >
                            {
                              row.associated.find((r) => {
                                const type = documentTypes.find(
                                  (d) => d.documentTypeId === r.documentTypeId,
                                )
                                return (
                                  r.documentTypeId === type?.documentTypeId &&
                                  type.documentCode ===
                                    DOCUMENT_TYPE_CODES.purchaseOrder
                                )
                              })?.serialNumber
                            }
                          </TableCell>
                          <TableCell
                            onClick={(e) => {
                              const document = row.associated.find((r) => {
                                const type = documentTypes.find(
                                  (d) => d.documentTypeId === r.documentTypeId,
                                )
                                return (
                                  r.documentTypeId === type?.documentTypeId &&
                                  type.documentCode ===
                                    DOCUMENT_TYPE_CODES.retentionReceipt
                                )
                              })
                              if (document) {
                                e.stopPropagation()
                                handleSelect(document)
                              }
                            }}
                            className="document-link"
                            padding="default"
                            align="left"
                          >
                            {
                              row.associated.find((r) => {
                                const type = documentTypes.find(
                                  (d) => d.documentTypeId === r.documentTypeId,
                                )
                                return (
                                  r.documentTypeId === type?.documentTypeId &&
                                  type.documentCode ===
                                    DOCUMENT_TYPE_CODES.retentionReceipt
                                )
                              })?.serialNumber
                            }
                          </TableCell>
                        </React.Fragment>
                      )}

                      {isRetentionReceipt() && (
                        <TableCell
                          onClick={(e) => {
                            const document = row.associated.find(
                              (r) =>
                                r.documentTypeId ===
                                  DOCUMENT_TYPE_IDS.electronicInvoice ||
                                r.documentTypeId ===
                                  DOCUMENT_TYPE_IDS.reimbursementInvoice ||
                                r.documentTypeId ===
                                  DOCUMENT_TYPE_IDS.foreignExpensesLiquidation ||
                                r.documentTypeId ===
                                  DOCUMENT_TYPE_IDS.printedInvoice,
                            )
                            if (document) {
                              e.stopPropagation()
                              handleSelect(document)
                            }
                          }}
                          className="document-link"
                          padding="default"
                          align="left"
                        >
                          {
                            row.associated.find(
                              (r) =>
                                r.documentTypeId ===
                                  DOCUMENT_TYPE_IDS.electronicInvoice ||
                                r.documentTypeId ===
                                  DOCUMENT_TYPE_IDS.reimbursementInvoice ||
                                r.documentTypeId ===
                                  DOCUMENT_TYPE_IDS.foreignExpensesLiquidation ||
                                r.documentTypeId ===
                                  DOCUMENT_TYPE_IDS.printedInvoice,
                            )?.serialNumber
                          }
                        </TableCell>
                      )}

                      {isReimbursementInvoice() && (
                        <RefundsCell documentId={row.documentId} />
                      )}
                    </TableRow>
                  )
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </div>

        <Box>
          <TablePagination
            rowsPerPageOptions={MEDIUM_ROWS_PER_PAGINATION_OPTIONS}
            component="div"
            count={totalDocuments}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={async (event, pageNumber) => {
              if (
                pageNumber > page &&
                (pageNumber + 1) * rowsPerPage > documentsTable.length &&
                documentsTable.length < totalDocuments
              ) {
                await handleNewPage(pageNumber, rowsPerPage)
              }
              handleChangePage(event, pageNumber)
            }}
            onRowsPerPageChange={async (event) => {
              if (
                parseInt(event.target.value, 10) > rowsPerPage &&
                parseInt(event.target.value, 10) > documentsTable.length &&
                documentsTable.length < totalDocuments
              ) {
                await handleNewPage(page, parseInt(event.target.value, 10))
              }
              handleChangeRowsPerPage(event as any)
            }}
            labelRowsPerPage="Documentos por página"
            labelDisplayedRows={LabelDisplayedRows}
          />
        </Box>
      </div>

      <Dialog open={successDialog} onClose={() => setSuccessDialog(false)} PaperProps={{ style: { borderRadius: 12, padding: "24px 32px", textAlign: "center", minWidth: 260 } }}>
        <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 12 }}>
          <Icon style={{ fontSize: 48, color: "#4caf50" }}>check_circle</Icon>
          <Typography variant="h6" style={{ fontWeight: 600 }}>Notificación enviada</Typography>
          <Typography variant="body2" style={{ color: "#666" }}>El correo fue enviado exitosamente</Typography>
        </div>
      </Dialog>

      <Snackbar
        open={messageConfig.open}
        autoHideDuration={6000}
        onClose={() => setMessageConfig({ ...messageConfig, open: false })}
        anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
      >
        <Alert
          variant="filled"
          onClose={() => setMessageConfig({ ...messageConfig, open: false })}
          severity={messageConfig.severity}
        >
          {messageConfig.message}
        </Alert>
      </Snackbar>
      <FixedLoadingIndicator loading={loading} />
      <DocumentPicker open={openPicker} onClose={() => setOpenPicker(false)} />
      <ServerErrorHandler
        error={error}
        onSuccess={() => setError(false)}
        tryAgain={getDocumentsByInitialFilters}
      />

      {/* ---- Dialogo para notificar al cliente  ----------- */}
      <Dialog open={open} onClose={handleCloseNotification} fullWidth>
        <DialogTitle>Notificación</DialogTitle>

        <DialogContent>
          <Typography gutterBottom>
            {emails.length === 1
              ? "Se procederá a notificar al correo"
              : "Se procederá a notificar a los correos"}
          </Typography>
          {emails.length === 0 && (
            <Typography color="error" variant="body2">
              Debe agregar al menos un correo para notificar
            </Typography>
          )}

          {/* Lista de correos */}
          <Box display="flex" flexWrap="wrap" mb={2}>
            {emails.map((email) => (
              <Box key={email} mr={1} mb={1}>
                <Chip
                  label={email}
                  onDelete={() => setEmails(emails.filter((e) => e !== email))}
                />
              </Box>
            ))}
          </Box>

          {/* TextField para agregar correo */}
          <Box display="flex" alignItems="flex-start" style={{ gap: 8 }}>
            <TextField
              label="Correo electrónico adicional"
              fullWidth
              size="small"
              value={newEmail}
              onChange={(e) => {
                setNewEmail(e.target.value)
                setErrorEmail("")
              }}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault()
                  handleAddEmail()
                }
              }}
              error={!!errorEmail}
              helperText={errorEmail || "Escribe un correo y presiona \"Agregar\""}
              InputProps={{}}
            />
            <Button
              variant="contained"
              color="primary"
              disableElevation
              size="small"
              onClick={handleAddEmail}
              style={{ marginTop: 4, whiteSpace: "nowrap", minWidth: 100 }}
              startIcon={<Icon fontSize="small">add</Icon>}
            >
              Agregar
            </Button>
          </Box>
        </DialogContent>

        <DialogActions>
          <Button onClick={handleCloseNotification}>Cancelar</Button>
          <Button
            color="primary"
            variant="contained"
            disabled={emails.length === 0 || sendingNotify}
            onClick={handleNotifyClient}
            startIcon={
              sendingNotify && <CircularProgress color="inherit" size={20} />
            }
          >
            Notificar
          </Button>
        </DialogActions>
      </Dialog>
    </Paper>
  )
}

export default DocumentsReceivedDashboard
